import { icon, refreshIcons } from '../icons';
import { parseFile, type ParsedStatement } from '@/parse';
import { addTransactionsBulk } from '@/storage/repo';
import { isDriveConfigured, pickFromDrive } from '@/lib/googleDrive';

const GOOGLE_G = `<svg viewBox="0 0 24 24" class="w-4 h-4" aria-hidden="true">
  <path fill="#4285F4" d="M23 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47c-.28 1.4-1.07 2.59-2.27 3.4v2.81h3.66c2.16-1.99 3.42-4.91 3.42-8.45z"/>
  <path fill="#34A853" d="M12 24c3.15 0 5.78-1.05 7.7-2.83l-3.66-2.81c-1.02.69-2.32 1.1-4.04 1.1-3.1 0-5.73-2.09-6.67-4.9H1.55v3.08A11.99 11.99 0 0 0 12 24z"/>
  <path fill="#FBBC05" d="M5.33 14.56A7.21 7.21 0 0 1 5 12c0-.89.12-1.75.33-2.56V6.36H1.55A11.99 11.99 0 0 0 0 12c0 1.94.47 3.77 1.55 5.64l3.78-3.08z"/>
  <path fill="#EA4335" d="M12 4.77c1.71 0 3.24.59 4.45 1.74l3.32-3.32C17.78 1.18 15.13 0 12 0 7.31 0 3.26 2.69 1.55 6.36l3.78 3.08C6.27 6.86 8.9 4.77 12 4.77z"/>
</svg>`;

export function renderUpload(root: HTMLElement) {
  root.innerHTML = `
    <main class="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <div>
        <h2 class="text-xl font-semibold">Import a bank statement</h2>
        <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Drop a Capitec or TymeBank/GoTyme statement (PDF or CSV). The file never leaves your device — parsing happens in your browser.
        </p>
      </div>

      <div id="dropzone" class="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-10 text-center bg-white/40 dark:bg-slate-900/40">
        <div class="inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 mb-3">
          ${icon('file-up', 'w-7 h-7')}
        </div>
        <p class="font-medium">Drop your statement here</p>
        <p class="text-xs text-slate-400 mt-3">PDF, CSV or TXT · up to 10 MB</p>
      </div>

      <div class="flex flex-col items-center gap-2">
        <p class="text-xs text-slate-500 dark:text-slate-400">or pick a source</p>
        <div class="flex flex-wrap items-center justify-center gap-2">
          <label class="inline-block">
            <input id="fileInput" type="file" accept=".pdf,.csv,.txt" class="hidden" />
            <span class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold cursor-pointer">
              ${icon('folder-open', 'w-4 h-4')} Choose file
            </span>
          </label>
          <button id="driveBtn" class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-semibold">
            <span>${GOOGLE_G}</span>
            <span>Open from Google Drive</span>
          </button>
        </div>
        <div id="driveHint" class="hidden text-xs text-amber-700 dark:text-amber-300 text-center">
          Google Drive needs an OAuth Client ID first. <a href="#/settings" class="underline font-medium">Open Settings</a> to paste one in.
        </div>
      </div>

      <div id="parseResult"></div>
    </main>
  `;
  refreshIcons();

  // Drive button visibility
  if (!isDriveConfigured()) {
    document.getElementById('driveHint')?.classList.remove('hidden');
  }
  document.getElementById('driveBtn')?.addEventListener('click', handleDrivePick);

  const dz = document.getElementById('dropzone')!;
  const input = document.getElementById('fileInput') as HTMLInputElement;
  ['dragenter', 'dragover'].forEach(ev =>
    dz.addEventListener(ev, e => { e.preventDefault(); dz.classList.add('border-indigo-500', 'bg-indigo-50/30'); }));
  ['dragleave', 'drop'].forEach(ev =>
    dz.addEventListener(ev, e => { e.preventDefault(); dz.classList.remove('border-indigo-500', 'bg-indigo-50/30'); }));
  dz.addEventListener('drop', e => {
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  });
  input.addEventListener('change', () => {
    const f = input.files?.[0];
    if (f) handleFile(f);
  });
}

async function handleDrivePick() {
  if (!isDriveConfigured()) {
    document.getElementById('driveHint')?.classList.remove('hidden');
    return;
  }
  const btn = document.getElementById('driveBtn') as HTMLButtonElement | null;
  if (btn) btn.disabled = true;
  const out = document.getElementById('parseResult')!;
  out.innerHTML = `
    <div class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60 p-5 text-sm text-slate-500">
      <div class="flex items-center gap-2"><span class="inline-block w-3 h-3 rounded-full bg-amber-500 animate-pulse"></span> Opening Google Drive…</div>
    </div>
  `;
  try {
    const result = await pickFromDrive();
    if (!result) { // user cancelled
      out.innerHTML = '';
      if (btn) btn.disabled = false;
      return;
    }
    await handleFile(result.file);
  } catch (err) {
    const msg = (err as Error).message || 'Unknown error';
    out.innerHTML = `
      <div class="rounded-2xl border border-rose-200 dark:border-rose-700/50 bg-rose-50/40 dark:bg-rose-900/20 p-5 text-sm text-rose-700 dark:text-rose-300">
        <div class="flex items-center gap-2">${icon('alert-triangle', 'w-4 h-4')} Google Drive error</div>
        <p class="mt-2 text-xs">${escapeHtml(msg)}</p>
        <p class="mt-2 text-xs">If this is a Client ID problem, open <a href="#/settings" class="underline font-medium">Settings</a> to update it.</p>
      </div>
    `;
    refreshIcons();
  } finally {
    if (btn) btn.disabled = false;
  }
}

async function handleFile(file: File) {
  const out = document.getElementById('parseResult')!;
  out.innerHTML = `
    <div class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60 p-5 text-sm text-slate-500">
      <div class="flex items-center gap-2"><span class="inline-block w-3 h-3 rounded-full bg-amber-500 animate-pulse"></span> Parsing ${escapeHtml(file.name)}…</div>
    </div>
  `;
  try {
    const stmt = await parseFile(file);
    renderResult(stmt, file.name, out);
  } catch (err) {
    out.innerHTML = `
      <div class="rounded-2xl border border-rose-200 dark:border-rose-700/50 bg-rose-50/40 dark:bg-rose-900/20 p-5 text-sm text-rose-700 dark:text-rose-300">
        <div class="flex items-center gap-2">${icon('alert-triangle', 'w-4 h-4')} Couldn't parse this file</div>
        <p class="mt-2 text-xs">${escapeHtml((err as Error).message)}</p>
      </div>
    `;
    refreshIcons();
  }
}

function renderResult(
  stmt: ParsedStatement,
  fileName: string,
  out: HTMLElement,
  saved?: { inserted: number; duplicates: number },
) {
  const bank = stmt.bank.charAt(0).toUpperCase() + stmt.bank.slice(1);
  const hasRows = stmt.transactions.length > 0;
  out.innerHTML = `
    <div class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60 p-5 space-y-4">
      <div class="flex items-center justify-between flex-wrap gap-2">
        <div class="flex items-center gap-2">
          ${icon('file-check-2', 'w-4 h-4 text-emerald-500')}
          <span class="font-semibold">${escapeHtml(fileName)}</span>
          <span class="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">${escapeHtml(bank)}</span>
        </div>
        <div class="text-sm text-slate-500 dark:text-slate-400">
          ${stmt.transactions.length} transaction${stmt.transactions.length === 1 ? '' : 's'}
          ${stmt.balanceMismatches.length ? `· <span class="text-rose-600 dark:text-rose-400">${stmt.balanceMismatches.length} balance warning${stmt.balanceMismatches.length === 1 ? '' : 's'}</span>` : ''}
        </div>
      </div>

      ${stmt.warnings.length ? `
        <details class="text-xs text-slate-500 dark:text-slate-400">
          <summary class="cursor-pointer">${stmt.warnings.length} parser warning${stmt.warnings.length === 1 ? '' : 's'}</summary>
          <ul class="mt-2 list-disc list-inside space-y-0.5">${stmt.warnings.slice(0, 8).map(w => `<li>${escapeHtml(w)}</li>`).join('')}</ul>
        </details>
      ` : ''}

      ${saved ? savedBlock(saved) : hasRows ? `
        <div class="flex flex-wrap items-center gap-2 pt-2">
          <button id="saveRawBtn" class="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">
            Save raw transactions
          </button>
          <button id="discardParseBtn" class="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800">
            Discard
          </button>
          <a href="#/review" class="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800">Review uploads</a>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400">Nothing is saved until you click "Save raw transactions". Duplicates of rows you already imported are skipped automatically.</p>
      ` : `
        <div class="space-y-3">
          <p class="text-sm text-rose-600 dark:text-rose-400">
            No transactions were extracted. Nothing was saved — this upload is discarded automatically.
          </p>
          <p class="text-xs text-slate-500 dark:text-slate-400">
            Check the file is a real statement export (not a scan), or try the CSV export from your banking app instead.
            To remove a previously saved upload, open <a href="#/review" class="underline font-medium">Review</a>.
          </p>
          <div class="flex items-center gap-2">
            <button id="discardParseBtn" class="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800">
              Discard & choose another file
            </button>
            <a href="#/review" class="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800">Manage saved uploads</a>
          </div>
        </div>
      `}
    </div>`;
  refreshIcons();

  const resetUploader = () => {
    out.innerHTML = '';
    const input = document.getElementById('fileInput') as HTMLInputElement | null;
    if (input) input.value = '';
  };
  document.getElementById('discardParseBtn')?.addEventListener('click', resetUploader);

  document.getElementById('saveRawBtn')?.addEventListener('click', async () => {
    if (!stmt.transactions.length) return;
    const outcome = await addTransactionsBulk(stmt.transactions.map(t => ({
      bank: stmt.bank,
      account: stmt.account,
      postedAt: t.date,
      description: t.description,
      amountCents: Math.round(t.amount * 100),
      balanceCents: t.balance === null ? null : Math.round(t.balance * 100),
      category: t.category,
      sourceFile: fileName,
    })));
    renderResult(stmt, fileName, out, outcome);
    document.getElementById('saveAgainBtn')?.addEventListener('click', resetUploader);
  });
}

function savedBlock(saved: { inserted: number; duplicates: number }): string {
  const allDup = saved.inserted === 0;
  const headline = allDup
    ? `All ${saved.duplicates} row${saved.duplicates === 1 ? ' was' : 's were'} already imported — nothing new added.`
    : `Saved ${saved.inserted} row${saved.inserted === 1 ? '' : 's'} to your device` +
      (saved.duplicates ? ` · skipped ${saved.duplicates} duplicate${saved.duplicates === 1 ? '' : 's'}` : '') + '.';
  return `
    <div class="rounded-xl border ${allDup
      ? 'border-amber-200 bg-amber-50/50 dark:border-amber-700/40 dark:bg-amber-900/20'
      : 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-700/40 dark:bg-emerald-900/20'} p-4 space-y-3">
      <div class="flex items-center gap-2 text-sm font-medium ${allDup
        ? 'text-amber-700 dark:text-amber-300'
        : 'text-emerald-700 dark:text-emerald-300'}">
        ${icon(allDup ? 'copy-minus' : 'check-circle-2', 'w-4 h-4')} ${escapeHtml(headline)}
      </div>
      <p class="text-xs text-slate-500 dark:text-slate-400">
        Duplicate matching uses bank + date + description + amount, so re-uploads and overlapping
        statement periods can't double-count your spending.
      </p>
      <div class="flex flex-wrap items-center gap-2">
        <button id="saveAgainBtn" class="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800">Back</button>
        <a href="#/suggest" class="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">View suggestions</a>
        <a href="#/review" class="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800">Review uploads</a>
      </div>
    </div>
  `;
}

function escapeHtml(s: string) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]!));
}
